/**
 * Storage Integrity Service Configuration
 * Default configurations and environment-specific settings
 * 
 * @version 1.1.0
 * @author SCM Security Team
 * @since Sprint 64
 */

import { StorageIntegrityConfig } from './storage-integrity.interfaces';

/**
 * Default configuration for StorageIntegrityService
 */
export const DEFAULT_STORAGE_CONFIG: StorageIntegrityConfig = {
  secretKey: 'scm-default-key-change-in-production',
  algorithm: 'sha256',
  strictMode: true,
  autoRecovery: true,
  keyPrefix: 'scm_',
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
  debug: false
};

/**
 * Storage format version - increment when making breaking changes
 */
export const STORAGE_FORMAT_VERSION = '1.1.0';



/**
 * Configuration validation rules
 */
export const CONFIG_VALIDATION = {
  MIN_SECRET_KEY_LENGTH: 16,
  MAX_SECRET_KEY_LENGTH: 256,
  MIN_MAX_AGE: 5 * 60 * 1000, // 5 minutes
  MAX_MAX_AGE: 7 * 24 * 60 * 60 * 1000, // 7 days
  VALID_ALGORITHMS: ['sha256', 'sha512'] as const
};

/**
 * Error messages for configuration validation
 */
export const CONFIG_ERRORS = {
  INVALID_SECRET_KEY: `La clave secreta debe tener entre ${CONFIG_VALIDATION.MIN_SECRET_KEY_LENGTH} y ${CONFIG_VALIDATION.MAX_SECRET_KEY_LENGTH} caracteres`,
  INVALID_ALGORITHM: `El algoritmo debe ser uno de: ${CONFIG_VALIDATION.VALID_ALGORITHMS.join(', ')}`,
  INVALID_MAX_AGE: `La expiración (maxAge) debe estar entre ${CONFIG_VALIDATION.MIN_MAX_AGE / 60000} minutos y ${CONFIG_VALIDATION.MAX_MAX_AGE / 86400000} días`,
  MISSING_SECRET_KEY: 'La clave secreta (secretKey) es obligatoria'
};

/**
 * Validate configuration object
 * @param config The configuration object to validate
 * @returns An object with a 'valid' boolean and an array of error messages.
 */
export function validateConfig(config: StorageIntegrityConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Validate secret key
  if (!config.secretKey) {
    errors.push(CONFIG_ERRORS.MISSING_SECRET_KEY);
  } else if (
    config.secretKey.length < CONFIG_VALIDATION.MIN_SECRET_KEY_LENGTH ||
    config.secretKey.length > CONFIG_VALIDATION.MAX_SECRET_KEY_LENGTH
  ) {
    errors.push(CONFIG_ERRORS.INVALID_SECRET_KEY);
  }
  
  // Validate algorithm
  if (config.algorithm && !CONFIG_VALIDATION.VALID_ALGORITHMS.includes(config.algorithm)) {
    errors.push(CONFIG_ERRORS.INVALID_ALGORITHM);
  }
  
  // Validate maxAge
  if (config.maxAge !== undefined && (
    config.maxAge < CONFIG_VALIDATION.MIN_MAX_AGE ||
    config.maxAge > CONFIG_VALIDATION.MAX_MAX_AGE
  )) {
    errors.push(CONFIG_ERRORS.INVALID_MAX_AGE);
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}